import { TailingIconTableCellComponent } from '../../components/tailing-icon-table-cell/tailing-icon-table-cell.component';
import { IPayment } from '@aca-new/app/pages/payments/shared/models/interfaces/payment.interface';
import { AbstractDefaultPaymentService } from '@aca-new/app/pages/payments/shared/services/abstract-default-payment/abstract-default-payment.service';
import { PaymentsService } from '@aca-new/app/pages/payments/shared/services/payment/payments.service';
import { CurrencyTableCellComponent } from '@aca-new/app/shared/components/currency-table-cell/currency-table-cell.component';
import { DataTableResponseType, IDataTableData, PageItemType } from '@aca-new/app/shared/components/data-table/shared/models/interfaces/data-table-data.interface';
import { DialogExportComponent } from '@aca-new/app/shared/components/modal/shared/components/dialog-export/dialog-export.component';
import { DialogService } from '@aca-new/app/shared/components/modal/shared/services/dialog.service';
import { ETableDataType } from '@aca-new/app/shared/components/table/shared/enums/table-data-type.enum';
import { ETableHeaderLayoutMode } from '@aca-new/app/shared/components/table/shared/enums/table-header-layout-mode.enum';
import { ITableBodyCell, ITableBodyRow } from '@aca-new/app/shared/components/table/shared/interfaces/table-data.interface';
import { ITableHeader } from '@aca-new/app/shared/components/table/shared/interfaces/table-header.interface';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { CHECKBOX, PO_NO, PRODUCT_NAME_MULTIPLE, REF_NO, SERVICE_DATE, SERVICE_TYPE, SUPPLIER } from '@aca-new/app/shared/constants/table-header.constants';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { IUrl } from '@aca-new/app/shared/models/interfaces/url.interface';
import { QimaIconService } from '@aca-new/app/shared/services/exported-services/qima-icon/qima-icon.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { Injectable } from '@angular/core';
import { EQimaTableHeadCellSort } from '@qima/ngx-qima';
import { assign, cloneDeep } from 'lodash/index';

@Injectable({
  providedIn: 'root',
})
export class InspectionPaymentService extends AbstractDefaultPaymentService implements IPayment {
  public readonly columns: ITableHeader[] = [
    CHECKBOX,
    assign(cloneDeep(SERVICE_TYPE), {
      sortKey: 'serviceTypeText',
      isFrontSort: true,
    }),
    assign(cloneDeep(SERVICE_DATE), {
      key: 'serviceDate',
      isFrontSort: true,
    }),
    assign(cloneDeep(PRODUCT_NAME_MULTIPLE), {
      isFrontSort: true,
    }),
    assign(cloneDeep(PO_NO), {
      isFrontSort: true,
    }),
    assign(cloneDeep(SUPPLIER), {
      key: 'factoryName',
      isFrontSort: true,
    }),
    assign(cloneDeep(REF_NO), {
      key: 'rfqNumber',
      isFrontSort: true,
      type: ETableDataType.CUSTOMIZED,
      cellTransform(cellData: ITableBodyCell, pageItem?: Record<string, PageItemType>): void {
        cellData.customizedCellConfiguration = {
          component: TailingIconTableCellComponent,
          data: {
            text: pageItem?.rfqNumber,
          },
        };
      },
    }),
  ];

  public readonly lastTwoColumns: ITableHeader[] = [
    {
      sortType: EQimaTableHeadCellSort.ALPHABETICALLY,
      key: 'proformaInvoiceNumberOrInvoiceNumber',
      isFrontSort: true,
      label: 'TABLE.INVOICE',
      type: ETableDataType.LINK,
      width: '202',
      cellClick: (cellData: Record<string, unknown>): void => {
        this._paymentsService.getInvoiceInfo(cellData.proformaInvoiceNumber as string, cellData);
      },
    },
    {
      sortType: EQimaTableHeadCellSort.ALPHABETICALLY,
      key: 'cost',
      label: 'TABLE.AMOUNT',
      type: ETableDataType.CUSTOMIZED,
      isFrontSort: true,
      mode: ETableHeaderLayoutMode.STICKY_RIGHT,
      sortFunction: (s1, s2, order): number => this.currencySortFunction(s1 as ITableBodyCell, s2 as ITableBodyCell, order),
      cellTransform(cellData: ITableBodyCell, pageItem): void {
        cellData.customizedCellConfiguration = {
          component: CurrencyTableCellComponent,
          data: {
            code: pageItem?.currency,
            value: pageItem?.cost,
          },
        };
      },
      width: '115',
    },
  ];

  public get userId(): string {
    return this._storageService.getItem(EStorageKeys.USER_ID);
  }

  public paidBookingsTableHeaders: ITableHeader[] = this._appUserSettingService.hideInspectionCostFromSuppliers()
    ? cloneDeep(this.columns)
    : cloneDeep(this.columns.concat(this.lastTwoColumns));

  public get paidBookingsTableUrl(): IUrl {
    return {
      url: `${SERVER_URL}/user/${this.userId}/payments?serviceCategory=INSPECTION&`,
      params: `paid=true&page=1&pagesize=10&sortType=desc&sortField=serviceDate&keyword=`,
    };
  }

  public unpaidBookingsTableHeaders: ITableHeader[] = cloneDeep(this.paidBookingsTableHeaders);

  public get unpaidBookingsTableUrl(): IUrl {
    return {
      url: `${SERVER_URL}/user/${this.userId}/payments?serviceCategory=INSPECTION&`,
      params: `paid=false&page=1&pagesize=10&sortType=desc&sortField=serviceDate&keyword=`,
    };
  }

  public constructor(
    public readonly qimaIcon: QimaIconService,
    private readonly _storageService: StorageService,
    private readonly _dialog: DialogService,
    private readonly _appUserSettingService: AppUserSettingService,
    _paymentsService: PaymentsService
  ) {
    super(_paymentsService);
  }

  public isEnablePayByTransfer(): boolean {
    return 'ADVANCE_PAYMENT' === this._appUserSettingService.getOnlinePaymentType();
  }

  public exportPaidBookings(): void {
    this._dialog.open(DialogExportComponent, {
      data: {
        filename: 'Inspections Paid Payment List({startDate} - {endDate}).xlsx',
        title: 'PAYMENTS.EXPORT_PAYMENT_LIST',
        url: `${SERVER_URL}/user/${this.userId}/payment-export?serviceType=INSPECTION&isPaid=yes&startDate={start}&endDate={end}`,
      },
    });
  }

  public exportUnpaidBookings(): void {
    this._dialog.open(DialogExportComponent, {
      data: {
        filename: 'Inspections UnPaid Payment List({startDate} - {endDate}).xlsx',
        title: 'PAYMENTS.EXPORT_PAYMENT_LIST',
        url: `${SERVER_URL}/user/${this.userId}/payment-export?serviceType=INSPECTION&isPaid=no&startDate={start}&endDate={end}`,
      },
    });
  }

  public transformSourceData(data: unknown, _pageSize: number): DataTableResponseType {
    return data as IDataTableData;
  }

  public getOrderNo(tableRow: Readonly<ITableBodyRow>): string {
    return (tableRow.originData as Record<string, string>).rfqNumber;
  }
}
